/**
 * PLP colour-family swatches on product cards.
 *
 * Clicking a swatch swaps the card's image gallery, price and quick-add form to
 * the selected colour's product in place (no navigation), and repoints the
 * card's image/title links to that colour's PDP. The swapped markup is fetched
 * once per colour via the Section Rendering API (section-rendering-card-gallery)
 * and cached, then the card's <slideshow-component> is morphed in place so it
 * stays swipeable, the price container is morphed to reflect the new colour's
 * price, and the <product-form-component> inside quick-add is replaced outright
 * so its hidden variant id input and inline size buttons submit the selected
 * colour's variants, not the card's original product's. The <quick-add-component>
 * wrapper's own attributes (title, add-vs-choose state) are kept in sync too, so
 * its mobile quick-add drawer reflects the selected colour.
 *
 * Plain <a href> links remain, so no-JS / middle-click / modifier-click still
 * navigate to the sibling product, and a failed fetch falls back to navigation.
 */
import { getViewParameterValue } from '@theme/utilities';
import { morph } from '@theme/morph';

const SECTION_ID = 'section-rendering-card-gallery';

/** @type {Map<string, Promise<Document>>} — cache/dedupe fetched galleries by path. */
const galleryCache = new Map();

/**
 * @param {string} pathname
 * @returns {string}
 */
function buildRequestUrl(pathname) {
  const next = new URL(pathname, window.location.origin);
  next.searchParams.set('section_id', SECTION_ID);
  const viewParam = getViewParameterValue();
  if (viewParam) next.searchParams.set('view', viewParam);
  return next.pathname + next.search;
}

/**
 * Fetch (and cache) the sibling product's card gallery as a parsed Document.
 * @param {string} pathname - The sibling product path (e.g. /products/handle)
 * @returns {Promise<Document>}
 */
function fetchGalleryDoc(pathname) {
  const cached = galleryCache.get(pathname);
  if (cached) return cached;

  const promise = fetch(buildRequestUrl(pathname))
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.text();
    })
    .then((text) => new DOMParser().parseFromString(text, 'text/html'))
    .catch((error) => {
      galleryCache.delete(pathname); // allow retry on next interaction
      throw error;
    });

  galleryCache.set(pathname, promise);
  return promise;
}

/**
 * Repoint the card's image/title links to the selected colour's PDP.
 * Mirrors ProductCard#updateProductUrl.
 *
 * `.color-swatch__more` is the "+N" link standing for the colourways past the card's
 * four-dot cap (snippets/color-swatch.liquid). It has to move with the rest: the card
 * now shows the sibling, so the link has to open the sibling's page, not the one the
 * card happened to render with.
 * @param {Element} card
 * @param {string} url
 */
function updateCardLinks(card, url) {
  for (const selector of [
    '[ref="productCardLink"]',
    '[ref="cardGalleryLink"]',
    '[ref="productTitleLink"]',
    '.color-swatch__more',
  ]) {
    const link = card.querySelector(selector);
    if (link instanceof HTMLAnchorElement) link.href = url;
  }
}

/**
 * Move the selected state to the clicked swatch within its swatch group.
 * @param {Element} group - the [data-color-swatch-card] container
 * @param {Element} selectedLink
 */
function setSelectedSwatch(group, selectedLink) {
  for (const link of group.querySelectorAll('a.color-swatch__link')) {
    const isSelected = link === selectedLink;
    link.classList.toggle('is-selected', isSelected);
    if (isSelected) {
      link.setAttribute('aria-current', 'true');
    } else {
      link.removeAttribute('aria-current');
    }
  }
}

/**
 * Swap the card's gallery to the sibling's by replacing the <slideshow-component>
 * outright, so it re-initialises as a fresh, swipeable slideshow. The outer
 * .card-gallery wrapper (and its aspect-ratio) is preserved.
 * @param {Element} card
 * @param {Document} siblingDoc
 * @returns {boolean} whether a swap happened
 */
function swapGallery(card, siblingDoc) {
  const currentSlideshow = card.querySelector('[ref="cardGallery"] slideshow-component');
  const newSlideshow = siblingDoc.querySelector('slideshow-component');
  if (!currentSlideshow || !newSlideshow) return false;

  currentSlideshow.replaceWith(document.importNode(newSlideshow, true));
  return true;
}

/**
 * Morph the card's price container to the sibling's, if both are present.
 * Optional: a card without a price block simply has nothing to swap.
 *
 * `morph` adopts nodes out of the tree it is given whenever the structures differ
 * (e.g. swapping a plain price for a sale price's compare-at markup), so it is given
 * an imported copy: `siblingDoc` is cached and reused for every later click on the
 * same colour, and morphing straight from it would strip the cached price markup and
 * leave the card's price empty the second time that colour is selected.
 * @param {Element} card
 * @param {Document} siblingDoc
 */
function swapPrice(card, siblingDoc) {
  const currentPrice = card.querySelector('product-price [ref="priceContainer"]');
  const newPrice = siblingDoc.querySelector('product-price [ref="priceContainer"]');
  if (!currentPrice || !newPrice) return;

  morph(currentPrice, document.importNode(newPrice, true));
}

/**
 * Swap the card's <product-form-component> (the hidden variant id input, the
 * inline size buttons, and the Add/Choose buttons) to the sibling's fetched one.
 *
 * This is the piece that actually gets submitted to the cart, and it is server-
 * rendered once at page load against the card's ORIGINAL product — nothing else
 * here re-derives it, so without this swap the form keeps pointing at the
 * original colour's variant ids no matter which sibling the shopper selects.
 * Load-bearing like `swapGallery`: a card with quick-add has to end this with a
 * form that matches the colour on screen, never the stale one.
 * @param {Element} card
 * @param {Document} siblingDoc
 * @returns {boolean} whether the card's quick-add form (if any) is now in sync
 */
function swapQuickAddForm(card, siblingDoc) {
  const currentForm = card.querySelector('quick-add-component product-form-component');
  if (!currentForm) return true; // no quick-add on this card - nothing to keep in sync

  const newForm = siblingDoc.querySelector('quick-add-component product-form-component');
  if (!newForm) return false; // quick-add exists here but the sibling's fetch didn't carry a form - unsafe to keep the stale one

  currentForm.replaceWith(document.importNode(newForm, true));
  return true;
}

/**
 * Sync the card's <quick-add-component> wrapper attributes to the sibling's so its
 * mobile quick-add drawer (title, add-vs-choose button state) reflects the selected
 * colour. Optional: a card without quick-add simply has nothing to sync.
 * @param {Element} card
 * @param {Document} siblingDoc
 */
function syncQuickAdd(card, siblingDoc) {
  const quickAdd = card.querySelector('quick-add-component');
  const newQuickAdd = siblingDoc.querySelector('quick-add-component');
  if (!quickAdd || !newQuickAdd) return;

  quickAdd.dataset.productTitle = newQuickAdd.dataset.productTitle || '';
  quickAdd.dataset.quickAddButton = newQuickAdd.dataset.quickAddButton || 'choose';
  quickAdd.dataset.productOptionsCount = newQuickAdd.dataset.productOptionsCount || '';
}

/**
 * @param {MouseEvent} event
 */
async function onDocumentClick(event) {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const target = event.target;
  if (!(target instanceof Element)) return;

  const group = target.closest('[data-color-swatch-card]');
  if (!group) return;

  const link = target.closest('a.color-swatch__link');
  if (!(link instanceof HTMLAnchorElement)) return;

  // Already-selected swatch: nothing to do, just don't navigate.
  if (link.classList.contains('is-selected') || link.getAttribute('aria-current') === 'true') {
    event.preventDefault();
    return;
  }

  const href = link.getAttribute('href');
  if (!href || href.startsWith('#')) return;

  const absolute = new URL(href, window.location.origin);
  if (absolute.origin !== window.location.origin) return;

  const card = group.closest('product-card');
  const gallery = card?.querySelector('[ref="cardGallery"]');
  if (!(card instanceof HTMLElement) || !gallery) return; // let the link navigate normally

  event.preventDefault();

  const pathname = absolute.pathname;
  // Guard against overlapping clicks on the same card: only the latest wins.
  card.dataset.pendingColourPath = pathname;
  gallery.setAttribute('aria-busy', 'true');
  setSelectedSwatch(group, link);

  try {
    const doc = await fetchGalleryDoc(pathname);
    if (card.dataset.pendingColourPath !== pathname) return; // superseded

    const swapped = swapGallery(card, doc);
    if (!swapped) throw new Error('No slideshow in sibling gallery response');

    // The quick-add form is what reaches the cart, so it has to end up correct or
    // not submittable - a failure here falls through to the outer catch, which
    // sends the shopper to the real sibling PDP instead of leaving a stale form.
    if (!swapQuickAddForm(card, doc)) {
      throw new Error('No quick-add form in sibling gallery response');
    }

    // The gallery and form are already showing/wired to the selected colour, so a
    // failure from here on is cosmetic - not worth yanking the shopper off the PLP
    // for - keep the selection and log it.
    try {
      swapPrice(card, doc);
      updateCardLinks(card, absolute.pathname + absolute.search);
      syncQuickAdd(card, doc);
    } catch (error) {
      console.error('Colour swatch card sync failed:', error);
    }
  } catch (error) {
    console.error('Colour swatch card swap failed:', error);
    window.location.assign(absolute.pathname + absolute.search);
  } finally {
    if (card.dataset.pendingColourPath === pathname) delete card.dataset.pendingColourPath;
    gallery.removeAttribute('aria-busy');
  }
}

/**
 * Prefetch a colour's gallery on hover so the click swap feels instant.
 * @param {PointerEvent} event
 */
function onPointerEnter(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const link = target.closest('[data-color-swatch-card] a.color-swatch__link');
  if (!(link instanceof HTMLAnchorElement)) return;
  if (link.classList.contains('is-selected')) return;

  const href = link.getAttribute('href');
  if (!href || href.startsWith('#')) return;
  const absolute = new URL(href, window.location.origin);
  if (absolute.origin !== window.location.origin) return;

  fetchGalleryDoc(absolute.pathname).catch(() => {}); // warm cache; ignore errors
}

document.addEventListener('click', onDocumentClick);
document.addEventListener('pointerenter', onPointerEnter, { capture: true });
